import React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useSearch } from "@/hooks/useSearch";

interface SearchableSelectProps<T extends Record<string, any>> {
  items: T[];
  searchFields: (keyof T)[];
  displayField: keyof T;
  placeholder?: string;
  emptyMessage?: string;
  value?: T;
  onSelect: (item: T) => void;
  className?: string;
  disabled?: boolean;
  getItemId?: (item: T) => string | number;
}

/**
 * Componente de select com busca reutilizável
 * 
 * @param props - Propriedades do componente
 * @returns Componente SearchableSelect
 */
export function SearchableSelect<T extends Record<string, any>>({
  items,
  searchFields,
  displayField,
  placeholder = "Selecione uma opção...",
  emptyMessage = "Nenhum item encontrado.",
  value,
  onSelect,
  className,
  disabled = false,
  getItemId = (item) => item.id || item.name,
}: SearchableSelectProps<T>) {
  const search = useSearch({
    items,
    searchFields,
  });

  React.useEffect(() => {
    if (value && (!search.selectedItem || getItemId(search.selectedItem) !== getItemId(value))) {
      search.setSelectedItem(value);
    }
  }, [value, search.selectedItem]);

  const handleSelect = (item: T) => {
    search.selectItem(item);
    onSelect(item);
  };

  return (
    <Popover open={search.isOpen} onOpenChange={search.setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={search.isOpen}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          {search.selectedItem || value
            ? String((search.selectedItem || value)?.[displayField])
            : placeholder}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput
            placeholder={`Buscar ${String(displayField).toLowerCase()}...`}
            className="h-9"
            value={search.searchTerm}
            onValueChange={search.setSearchTerm}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {search.filteredItems.map((item) => {
                const itemId = getItemId(item);
                const isSelected = search.selectedItem 
                  ? getItemId(search.selectedItem) === itemId
                  : value ? getItemId(value) === itemId : false;

                return (
                  <CommandItem
                    key={String(itemId)}
                    value={String(item[displayField])}
                    onSelect={() => handleSelect(item)}
                  >
                    {String(item[displayField])}
                    <Check
                      className={cn(
                        "ml-auto h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0"
                      )}
                    />
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export type { SearchableSelectProps }; 